import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "@/lib/constants";
import { Reveal } from "@/components/Reveal";

function Counter({ value, suffix }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{n}{suffix}</span>;
}

export default function WhyUs() {
  return (
    <section className="relative section-pad overflow-hidden" data-testid="why-us-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-6">
            <div className="overline mb-4">03 · Why LaneX</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Enterprise craft.<br /><span className="italic">Startup velocity.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-[15px] text-[color:var(--text-muted)] leading-relaxed">
              We're the digital partner ambitious teams keep on speed-dial — trusted
              across sectors, benchmarked against the world's best.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 border-t border-l border-[color:var(--border)]">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.05}>
              <div className="border-r border-b border-[color:var(--border)] p-8 lg:p-10 min-h-[180px] flex flex-col justify-between">
                <div className="overline text-[10.5px]">{s.label}</div>
                <div className="font-serif text-5xl lg:text-6xl tracking-tight mt-6 text-gradient">
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Trust markers */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["ISO-aligned", "Security & privacy"],
            ["WCAG 2.2", "Accessibility"],
            ["Sub-second", "Load performance"],
            ["24h reply", "Enquiry SLA"],
          ].map(([t, s]) => (
            <div key={t} className="flex flex-col">
              <div className="font-serif text-2xl">{t}</div>
              <div className="mt-1 text-[12px] text-[color:var(--text-muted)] font-mono tracking-wider uppercase">{s}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
